
import { JSX, useEffect, useRef } from "react";
import Button from "../buttons/basic"
import SvgClose from "../svgs/close";
import SvgSave from "../svgs/save";
import SvgDelete from "../svgs/delete";
import useTranslation from "../../hooks/useTranslate.hook";
import { Colors } from "@/enums/colors.enum";

type ModalType = {
    isOpen: Boolean,
    closeModal: Function,
    title?: string | React.ReactNode,
    cancelButton?: ButtonTypes,
    submitButton?: ButtonTypes,
    deleteButton?: ButtonTypes, 
    children: React.ReactNode,
    //added extra-large as a huge variant that doesn't automatically take up max screen height
    size?: "small" | "smallish" | "medium" | "large" | "extra-large" | "huge" | "massive",
    isChildOpen?: Boolean;
    hasBackground?: Boolean;
    classConfig?: any;
    icon?: any;
    iconOnClick?: Function;
    showClose?: Boolean;
    footerChildren?: React.ReactNode;
    closeOnEscape?: Boolean;
}

type ButtonTypes = false | "cancel" | "save" | "delete";

const Modal = ({ isOpen, closeModal, classConfig, title, cancelButton, submitButton, deleteButton, size, children, isChildOpen, hasBackground = true, icon, iconOnClick, showClose = true, footerChildren, closeOnEscape = true}: ModalType) => {
    const { t } = useTranslation();
    const modalRef = useRef<any>(null);

    const buttons = {
        delete: <Button customClasses="c-modal-delete" shape="round" icon={[<SvgDelete key="svg-delete" />]} variant="contained" color="error" onClick={() => closeModal("delete")} text={t("delete")}></Button>,
        cancel: <Button shape="round" icon={[<SvgClose key="svg-remove" />]} variant="outlined" color="error" onClick={() => closeModal(false)} text={t("cancel")}></Button>,
        save: <Button shape="round" icon={[<SvgSave key="svg-save" />]} variant="contained" color="success" onClick={() => closeModal(true)} text={t("save")}></Button>
    };

    const getButton = (button: ButtonTypes | undefined): JSX.Element | null => {
        switch (button) {
            case "delete":
                return buttons.delete;
            case "cancel":
                return buttons.cancel;
            case "save":
                return buttons.save;
            default:
                return null;
        }
    };

    useEffect(() => {
        if (isOpen) {
            document.body.classList.add('u-no-scroll');
        }
        const close = (e: any) => {
            if (e.key === "Escape" && closeOnEscape) {
                closeModal();
            }
        }
        window.addEventListener('keydown', close);

        return () => { 
            window.removeEventListener('keydown', close);
            document.body.classList.remove('u-no-scroll');
        };
    }, [isOpen, closeModal]);

    useEffect(() => {
        if (isOpen && modalRef.current) {
            modalRef.current.focus();
        }
    }, [isOpen]);

    if (!isOpen) {
        return <></>
    }

    return (
        <div className={`c-modal-background ${hasBackground ? "c-modal-background-overlay" : ""} ${isChildOpen ? "u-hide-visually" : ""}`} role="dialog" tabIndex={-1} aria-modal={true} ref={modalRef}>
            <div className={`c-modal-container c-modal-container--${size} ${!hasBackground ? "c-modal-container-border" : ""} ${classConfig} ${footerChildren ? "c-modal-container--has-footer-children" : ""}`} >
                {title ? <div className={`c-modal-title c-modal-title--${size}`}>
                    {!!icon && (!!iconOnClick ? <Button size="link" color="grey" icon={icon} onClick={iconOnClick}/> : <div className="c-modal-title-icon">{icon}</div>)}
                    <div className="c-modal-title-text"><strong className="u-word-break-word">{title}</strong></div>
                    {showClose && <div className={`c-modal-title-close c-modal-title-close--${size}`}>
                        <Button 
                            size={(size == "huge" || size == "massive") ? "large" : "medium"} 
                            color={(size == "huge" || size == "massive") ? "grey" : "default"}
                            justify="center" shape="round" 
                            icon={[<SvgClose key="svg-close" />]} 
                            onClick={() => closeModal(false)} 
                            customClasses="c-modal-title-close--svg"/>
                    </div>}
                </div> : <></>}
                <div className={`c-modal-content ${!title && "c-modal-content--no-title"}`}>
                    <div className={`c-modal-body ${footerChildren ? "c-modal-body--footer-children" : ""}`}>
                        {children}
                    </div>
                    {(cancelButton || submitButton || deleteButton) && 
                        <div className={`c-modal-footer c-modal-footer--${size}`}>
                            {getButton(deleteButton)}
                            {getButton(cancelButton)}
                            {getButton(submitButton)}
                        </div>
                    }
                    {footerChildren && 
                        <div className={`c-modal-footer-children`}>
                            {footerChildren}
                        </div>
                    }
                </div>
            </div>
        </div>
    )
}

export default Modal;
